// เอกสารสัญญา — payload builders for the two Google Apps Script contract
// generators (สัญญาจ้างงาน / สัญญาฝึกอบรม). Rendered by two SEPARATE Apps Script
// web apps, not by the CRM's own PDF renderer, so the field names here are not
// ours to choose — each one is the literal {{placeholder}} in that contract's
// Google Doc template. See PDF/hiring_employee/README.md.
//
// ⚠️ The two templates DISAGREE on three names for the same thing. They were
// written a day apart and nothing has ever sent to both, so the drift was never
// felt. Do not "tidy" these — a renamed key renders as an empty gap in a signed
// contract, silently:
//
//     concept          สัญญาจ้างงาน        สัญญาฝึกอบรม
//     เลขบัตรประชาชน    employee_idcard    employee_id_card
//     ผู้ออกสัญญา (HR)   issue_HR           HR_name
//     คำนำหน้า          (inside employee_name)  employee_prefix
//
// The employment template also uses {{date}} TWICE — once as วันที่ทำสัญญา and
// again in ข้อ 2.1 as the day the contract takes effect. `date_start` is sent as
// well so that paragraph can be split later without touching this file; today it
// resolves to nothing and ข้อ 2.1 keeps showing the contract date.

import { parseDate, toDateInput } from './asset.js'
import { salaryEntry } from './employee.js'
import { pickCompany } from './wht.js'

export const EMPLOYMENT = 'employment'
export const TRAINING = 'training'

// Ordered the way the two are issued: ฝึกอบรม is signed before hiring and
// สัญญาจ้างงาน only after ข้อ 6 ("ผู้จัดอบรมอาจเสนอทำสัญญาจ้างงานแยกต่างหาก")
// is satisfied. The picker follows this order and opens on the first entry.
export const CONTRACT_TYPES = [
  { key: TRAINING, label: 'สัญญาฝึกอบรม', filePrefix: 'สัญญาฝึกอบรม' },
  { key: EMPLOYMENT, label: 'สัญญาจ้างงาน', filePrefix: 'สัญญาจ้างงาน' },
]

export const DEFAULT_CONTRACT = CONTRACT_TYPES[0].key

export const contractLabel = (key) => CONTRACT_TYPES.find((t) => t.key === key)?.label || ''

/* ── ตำแหน่ง ─────────────────────────────────────────────────────────────── */

// The ONLY four values ข้อ 5 (หน้าที่และความรับผิดชอบ) has a {{#if}} block for.
// Anything else — including a value that merely looks close — matches no block,
// and the Apps Script deletes every unmatched block: the contract then prints
// ข้อ 5 with a heading and no duties under it. That is the single worst way this
// generator can fail, because the PDF still looks finished.
export const CONTRACT_POSITIONS = [
  'พนักงานต้อนรับ',
  'เจ้าหน้าที่ลูกค้าสัมพันธ์',
  'ผู้จัดการประจำสาขา',
  'ผู้ช่วยทันตแพทย์',
]

// Employee.position (the CRM enum) → the contract's wording, for the three that
// are unambiguously the same job. The CRM's other positions (Admin, Sale, Lab,
// ช่างทันตกรรม, Graphic, Messenger, แม่บ้าน, HR, CEO, อื่นๆ) have NO ข้อ 5 block
// written for them, so they are deliberately absent: HR is asked to choose
// rather than handed a guess. Adding a row here is a clinic decision — it means
// asserting that two job titles carry the same contractual duties.
export const POSITION_TO_CONTRACT = {
  Counter: 'พนักงานต้อนรับ',
  ผู้จัดการ: 'ผู้จัดการประจำสาขา',
  ผู้ช่วยทันตแพทย์: 'ผู้ช่วยทันตแพทย์',
}

export const contractPositionFor = (position) => POSITION_TO_CONTRACT[String(position || '').trim()] || ''

/* ── dates ───────────────────────────────────────────────────────────────── */

const TH_MONTHS = [
  'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
  'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม',
]

// "2026-04-28" → "28 เมษายน 2569". Full month name and พ.ศ. — the form a Thai
// contract is dated in. (asset.js#fmtDateTH abbreviates and is for tables.)
export function fmtDateFullTH(v) {
  const d = parseDate(v)
  if (!d) return ''
  return `${d.getDate()} ${TH_MONTHS[d.getMonth()]} ${d.getFullYear() + 543}`
}

export function todayInput() {
  const d = new Date()
  const pad = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

/* ── salary steps ────────────────────────────────────────────────────────── */

// The contract's เงินเดือน is a STEP LADDER by month of service
// ({from, to, amount} — "เดือนที่ 1", "เดือนที่ 2-6", "ตั้งแต่เดือนที่ 7"), which is
// not what Employee.salary[] holds (that is a flat named list the payroll reads:
// Base Salary + Position Fee). So this seeds one open-ended step at the current
// Base Salary and lets HR build the ladder — the two are different facts and
// deriving one from the other would be a guess about the offer being made.
export function seedSalarySteps(employee) {
  const base = Number(salaryEntry(employee, 'Base Salary')?.amount || 0)
  return [{ from: '1', to: '', amount: base ? String(base) : '' }]
}

export const emptyStep = () => ({ from: '', to: '', amount: '' })

// Drop blank rows and coerce to what the Apps Script expects: `to: null` is an
// open-ended final step ("ตั้งแต่เดือนที่ N เป็นต้นไป"), not a missing value.
export function cleanSalarySteps(steps) {
  return (Array.isArray(steps) ? steps : [])
    .map((s) => ({
      from: intOrNull(s?.from),
      to: intOrNull(s?.to),
      amount: Number(String(s?.amount ?? '').replace(/,/g, '')),
    }))
    .filter((s) => Number.isFinite(s.amount) && s.amount > 0)
}

function intOrNull(v) {
  const s = String(v ?? '').trim()
  if (!s) return null
  const n = parseInt(s, 10)
  return Number.isFinite(n) ? n : null
}

/* ── prefill ─────────────────────────────────────────────────────────────── */

// Everything the two forms start with, read off the employee record and their
// branch. Every field stays editable — a contract is a negotiated document and
// the record is only the best first guess at it.
export function seedForm(employee, branch, hrName) {
  const c = pickCompany(branch)
  const e = employee || {}
  return {
    date: todayInput(),
    date_start: toDateInput(e.date_start),
    date_end: toDateInput(e.date_end),

    company_name: c.name,
    company_address: c.address,
    company_taxid: c.taxid,

    clinic_name: String(branch?.name || '').trim(),
    clinic_address: String(branch?.address || '').trim(),

    employee_prefix: String(e.prefix || '').trim(),
    employee_name: String(e.name || '').trim(),
    employee_idcard: String(e.idcardnumber || '').trim(),
    employee_address: String(e.address || '').trim(),
    employee_phone: String(e.phone || '').trim(),
    employee_position: contractPositionFor(e.position),

    hr_name: String(hrName || '').trim(),
    salary_steps: seedSalarySteps(e),
  }
}

/* ── payloads ────────────────────────────────────────────────────────────── */

// สัญญาจ้างงาน. The template has no {{employee_prefix}} — the intro reads
// "…กับ {{employee_name}} เลขประจำตัวประชาชน…", so the prefix has to travel
// inside the name or the sentence loses its คำนำหน้า.
export function buildEmploymentPayload(form) {
  return {
    date: fmtDateFullTH(form.date),
    date_start: fmtDateFullTH(form.date_start),

    company_name: t(form.company_name),
    company_address: t(form.company_address),
    company_taxid: t(form.company_taxid),

    employee_name: joinPrefix(form.employee_prefix, form.employee_name),
    employee_idcard: t(form.employee_idcard),
    employee_address: t(form.employee_address),
    employee_phone: t(form.employee_phone),
    employee_position: t(form.employee_position),

    clinic_name: t(form.clinic_name),
    clinic_address: t(form.clinic_address),

    salary_steps: cleanSalarySteps(form.salary_steps),
    issue_HR: t(form.hr_name),
  }
}

// สัญญาฝึกอบรม. Shorter: no address, no position, no salary — ข้อ 1 states in so
// many words that this is not employment. `employee_id_card` and `HR_name` are
// this template's spellings; see the drift table at the top of the file.
export function buildTrainingPayload(form) {
  return {
    date: fmtDateFullTH(form.date),
    date_start: fmtDateFullTH(form.date_start),
    date_end: fmtDateFullTH(form.date_end),

    company_name: t(form.company_name),
    company_address: t(form.company_address),

    employee_prefix: t(form.employee_prefix),
    employee_name: t(form.employee_name),
    employee_id_card: t(form.employee_idcard),
    employee_phone: t(form.employee_phone),

    HR_name: t(form.hr_name),
  }
}

export function buildPayload(type, form) {
  return type === TRAINING ? buildTrainingPayload(form) : buildEmploymentPayload(form)
}

const t = (v) => String(v ?? '').trim()

// "น.ส." + "สมหญิง ใจดี" → "น.ส.สมหญิง ใจดี". Thai prefixes run into the name
// with no space. If the name already opens with the prefix, don't double it —
// this generator has been bitten by a doubled name once already.
export function joinPrefix(prefix, name) {
  const p = t(prefix)
  const n = t(name)
  if (!p) return n
  if (!n) return p
  return n.startsWith(p) ? n : `${p}${n}`
}

/* ── validation ──────────────────────────────────────────────────────────── */

export const IDCARD_LEN = 13
export const PHONE_LEN = 10

// Returns '' when the form may be sent, otherwise the one message to show. Order
// matters: it reads top-to-bottom down the form so the error always points at
// the first field the eye reaches.
export function validate(type, form) {
  const f = form || {}
  if (!f.date) return 'กรุณาระบุวันที่ทำสัญญา'

  if (!t(f.company_name)) return 'กรุณาระบุชื่อบริษัท'
  if (!t(f.company_address)) return 'กรุณาระบุที่อยู่บริษัท'
  if (type === EMPLOYMENT && !t(f.company_taxid)) return 'กรุณาระบุเลขทะเบียนนิติบุคคล'

  // These three are read-only in the dialog now — they are the personnel record's
  // identity, not a per-contract choice — so the message has to name the place they
  // CAN be fixed. "กรุณาระบุ…" would be asking for something the form cannot accept.
  const FIX_ON_RECORD = ' — แก้ที่ประวัติพนักงานก่อนออกสัญญา'
  if (!t(f.employee_name)) return 'ประวัติพนักงานยังไม่มีชื่อ-สกุล' + FIX_ON_RECORD
  const id = t(f.employee_idcard)
  if (id.length !== IDCARD_LEN) return `เลขบัตรประชาชนต้องเป็นตัวเลข ${IDCARD_LEN} หลัก${FIX_ON_RECORD}`
  const phone = t(f.employee_phone)
  if (phone.length !== PHONE_LEN) return `เบอร์โทรต้องเป็นตัวเลข ${PHONE_LEN} หลัก${FIX_ON_RECORD}`

  if (type === EMPLOYMENT) {
    if (!t(f.employee_address)) return 'กรุณาระบุที่อยู่พนักงาน'
    // Refused rather than warned: an unlisted position prints ข้อ 5 empty, and a
    // contract with no duties section is worse than no contract.
    if (!CONTRACT_POSITIONS.includes(t(f.employee_position))) {
      return 'กรุณาเลือกตำแหน่งตามสัญญา (ข้อ 5 หน้าที่และความรับผิดชอบ จะว่างเปล่าถ้าไม่ตรง)'
    }
    if (!t(f.clinic_name)) return 'กรุณาระบุชื่อสถานที่ปฏิบัติงาน'
    if (!t(f.clinic_address)) return 'กรุณาระบุที่อยู่สถานที่ปฏิบัติงาน'
    if (cleanSalarySteps(f.salary_steps).length === 0) return 'กรุณาระบุเงินเดือนอย่างน้อย 1 ขั้น'
  }

  if (type === TRAINING) {
    if (!f.date_start) return 'กรุณาระบุวันเริ่มฝึกอบรม'
    if (!f.date_end) return 'กรุณาระบุวันสิ้นสุดการฝึกอบรม'
    if (f.date_end < f.date_start) return 'วันสิ้นสุดการฝึกอบรมต้องอยู่หลังวันเริ่ม'
  }

  if (!t(f.hr_name)) return 'กรุณาระบุชื่อเจ้าหน้าที่ฝ่ายทรัพยากรบุคคล'
  return ''
}

// Non-blocking notes shown above the button. These are the things that produce a
// contract which is wrong but not obviously wrong, so they are said out loud
// instead of being fixed silently.
export function warnings(type, form) {
  const out = []
  const f = form || {}
  if (type === EMPLOYMENT && f.date_start && f.date && f.date_start !== f.date) {
    out.push('ข้อ 2.1 (วันที่สัญญามีผล) จะแสดงวันที่ทำสัญญา ไม่ใช่วันเริ่มงาน — เทมเพลตยังใช้ตัวแปรเดียวกัน')
  }
  if (type === TRAINING && f.date_start && f.date_end) {
    const days = workingDaysBetween(f.date_start, f.date_end)
    if (days != null && days !== 10) {
      out.push(`ข้อ 2 ระบุ "10 วันทำการ" ตายตัวในเทมเพลต แต่ช่วงวันที่ที่เลือกได้ ${days} วันทำการ`)
    }
  }
  return out
}

// Mon–Fri count, inclusive. Public holidays are not modelled — this only exists
// to catch a date range that plainly disagrees with the template's hardcoded
// "10 วันทำการ", so it is a prompt to look, never a number the contract prints.
export function workingDaysBetween(startISO, endISO) {
  const a = parseDate(startISO)
  const b = parseDate(endISO)
  if (!a || !b || b < a) return null
  let n = 0
  const d = new Date(a.getFullYear(), a.getMonth(), a.getDate())
  const end = new Date(b.getFullYear(), b.getMonth(), b.getDate())
  while (d <= end) {
    const wd = d.getDay()
    if (wd !== 0 && wd !== 6) n++
    d.setDate(d.getDate() + 1)
  }
  return n
}
